import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Heart, Users, UserMinus, ArrowLeft } from 'lucide-react';
import { useAuthenticatedUser } from '@/hooks/useAuthenticatedUser';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useNavigate } from 'react-router-dom';
import { getUserTypeDisplayName, getUserTypeBadgeVariant } from '@/utils/userTypeDisplay';

interface FollowedUser {
  id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
  role: string;
  profile_photo_url?: string;
  user_type: 'supabase' | 'internal';
  followed_at: string;
}

const ManageFollowing: React.FC = () => {
  const { user, supabaseUser, internalUser } = useAuthenticatedUser();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [followedUsers, setFollowedUsers] = useState<FollowedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [unfollowingUsers, setUnfollowingUsers] = useState<Set<string>>(new Set());

  const currentUserId = user?.id;
  const currentUserType = supabaseUser ? 'supabase' : 'internal';

  const loadFollowedUsers = async () => {
    if (!user || !currentUserId) return;
    
    setLoading(true);
    try {
      // Get users that current user follows
      const { data: follows, error: followsError } = await supabase
        .from('user_follows')
        .select('followed_id, followed_type, followed_at')
        .eq('follower_id', currentUserId)
        .eq('follower_type', currentUserType)
        .eq('is_active', true);

      if (followsError) {
        console.error('Error fetching follows:', followsError);
        setFollowedUsers([]);
        return;
      }

      if (!follows || follows.length === 0) {
        setFollowedUsers([]);
        return;
      }

      // Get user details for followed users
      const supabaseUserIds = follows.filter(f => f.followed_type === 'supabase').map(f => f.followed_id);
      const internalUserIds = follows.filter(f => f.followed_type === 'internal').map(f => f.followed_id);

      const followedUsersData: FollowedUser[] = [];

      // Fetch both user types in parallel for better performance
      const [supabaseResult, internalResult] = await Promise.all([
        supabaseUserIds.length > 0
          ? supabase
              .from('profiles')
              .select('id, username, first_name, last_name, role, profile_photo_url')
              .in('id', supabaseUserIds)
          : Promise.resolve({ data: null, error: null }),
        internalUserIds.length > 0
          ? supabase
              .from('internal_users')
              .select('id, username, role, internal_user_profiles(first_name, last_name, profile_photo_url)')
              .in('id', internalUserIds)
          : Promise.resolve({ data: null, error: null })
      ]);

      // Process Supabase users
      if (!supabaseResult.error && supabaseResult.data) {
        supabaseResult.data.forEach(user => {
          const follow = follows.find(f => f.followed_id === user.id && f.followed_type === 'supabase');
          if (follow) {
            followedUsersData.push({
              ...user,
              user_type: 'supabase',
              followed_at: follow.followed_at
            });
          }
        });
      }

      // Process internal users
      if (!internalResult.error && internalResult.data) {
        internalResult.data.forEach((user: any) => {
          const follow = follows.find(f => f.followed_id === user.id && f.followed_type === 'internal');
          if (follow) {
            // Extract profile data from the joined result
            const profile = Array.isArray(user.internal_user_profiles)
              ? user.internal_user_profiles[0]
              : user.internal_user_profiles;

            const rawPhotoPath: string | null | undefined = profile?.profile_photo_url;
            const photoUrl = rawPhotoPath
              ? (rawPhotoPath.startsWith('http')
                  ? rawPhotoPath
                  : supabase.storage
                      .from('profile-photos')
                      .getPublicUrl(rawPhotoPath)
                      .data?.publicUrl)
              : undefined;

            followedUsersData.push({
              id: user.id,
              username: user.username,
              first_name: profile?.first_name || user.username,
              last_name: profile?.last_name || '',
              role: user.role,
              user_type: 'internal',
              followed_at: follow.followed_at,
              profile_photo_url: photoUrl
            });
          }
        });
      }

      // Sort by followed date (most recent first)
      followedUsersData.sort((a, b) => new Date(b.followed_at).getTime() - new Date(a.followed_at).getTime());
      
      setFollowedUsers(followedUsersData);
    } catch (error) {
      console.error('Error loading followed users:', error);
      setFollowedUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && currentUserId && currentUserType) {
      loadFollowedUsers();
    }
  }, [user, currentUserId, currentUserType]);

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto p-6 pt-20">
          <Card className="bg-card border-border">
            <CardContent className="p-6 text-center">
              <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Please Log In</h2>
              <p className="text-muted-foreground">
                You need to be logged in to manage who you're following
              </p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  const handleUnfollow = async (targetUserId: string, targetUserType: 'supabase' | 'internal', targetUserName: string) => {
    const userKey = `${targetUserType}-${targetUserId}`;
    setUnfollowingUsers(prev => new Set(prev).add(userKey));

    try {
      const { error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', currentUserId)
        .eq('follower_type', currentUserType)
        .eq('followed_id', targetUserId)
        .eq('followed_type', targetUserType);

      if (error) throw error;

      // Remove from local state
      setFollowedUsers(prev => prev.filter(user => 
        !(user.id === targetUserId && user.user_type === targetUserType)
      ));

      toast({
        title: "Unfollowed",
        description: `You are no longer following ${targetUserName}`,
      });
    } catch (error) {
      console.error('Error unfollowing user:', error);
      toast({
        title: "Error",
        description: "Failed to unfollow user",
        variant: "destructive"
      });
    } finally {
      setUnfollowingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userKey);
        return newSet;
      });
    }
  };

  const getRoleColor = (role: string): string => {
    const roleColors: { [key: string]: string } = {
      'player': 'bg-blue-100 text-blue-800',
      'coach': 'bg-green-100 text-green-800',
      'venue': 'bg-purple-100 text-purple-800',
      'team-owner': 'bg-orange-100 text-orange-800',
      'scorekeeper': 'bg-yellow-100 text-yellow-800',
      'fan': 'bg-pink-100 text-pink-800',
      'parent': 'bg-indigo-100 text-indigo-800',
      'admin': 'bg-red-100 text-red-800'
    };
    return roleColors[role] || 'bg-gray-100 text-gray-800';
  };

  const getDisplayName = (user: FollowedUser): string => {
    if (user.username) return user.username;
    if (user.first_name && user.last_name) return `${user.first_name} ${user.last_name}`;
    if (user.first_name) return user.first_name;
    return 'Unknown User';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto p-6 pt-20">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Heart className="h-6 w-6 text-primary" />
                </div>
                Manage Following
              </h1>
              <p className="text-muted-foreground mt-2">
                Manage the people and accounts you follow
              </p>
            </div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/feed')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Feed
            </Button>
          </div>
        </div>

        {/* Stats Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Following {followedUsers.length} Users</h3>
                  <p className="text-sm text-muted-foreground">
                    You'll see activities from these users in your feed
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/find-people')}
                variant="default"
              >
                Find More People
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Following List */}
        <div className="space-y-4">
          {loading && (
            <Card>
              <CardContent className="p-6 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading your followed users...</p>
              </CardContent>
            </Card>
          )}

          {!loading && followedUsers.length === 0 && (
            <Card>
              <CardContent className="p-6 text-center">
                <Heart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Not Following Anyone Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Start following people to see their activities in your feed
                </p>
                <Button 
                  onClick={() => navigate('/find-people')}
                  variant="default"
                >
                  Find People to Follow
                </Button>
              </CardContent>
            </Card>
          )}

          {!loading && followedUsers.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {followedUsers.map((user) => {
                const userKey = `${user.user_type}-${user.id}`;
                const isUnfollowing = unfollowingUsers.has(userKey);
                
                return (
                  <Card key={userKey} className="bg-card border-border">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={user.profile_photo_url} alt={`${getDisplayName(user)} profile photo`} />
                          <AvatarFallback>
                            {getDisplayName(user).substring(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm truncate">
                            {getDisplayName(user)}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className={getRoleColor(user.role)}>
                              {user.role}
                            </Badge>
                            <Badge variant={getUserTypeBadgeVariant(user.user_type)} className="text-xs">
                              {getUserTypeDisplayName(user.user_type)}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Following since {formatDate(user.followed_at)}
                          </p>
                          <div className="mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnfollow(user.id, user.user_type, getDisplayName(user))}
                              disabled={isUnfollowing}
                              className="w-full"
                            >
                              <UserMinus className="h-4 w-4 mr-2" />
                              {isUnfollowing ? 'Unfollowing...' : 'Unfollow'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ManageFollowing;